import {Component, OnInit} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {TransactionsState} from '../../../../shared/states/transactions/transactions.state';
import {Observable} from 'rxjs/internal/Observable';
import {getDateFromISO} from '../../../../shared/helpers/helpers';
import {
  SetDrugTransactions,
} from '../../../../shared/states/transactions/transactions.actions';
import {ActivatedRoute, Router} from '@angular/router';
import {BadgeColorsEnum} from '../../../../shared/interfaces/BadgeColorsEnum';
import {ActivityTypesEnum} from '../../../../shared/interfaces/ActivityTypesEnum';
import {ITransaction} from '../../../../shared/interfaces/transaction.interface';
import {BaseTableDirective} from '../../../../shared/directives/base-table.directive';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { GetCurrentInventoryTransactions, SetCurrentFilteredInventoryTransactions } from 'src/app/shared/states/inventory/inventory.actions';
import {TransactionsService} from '../../../../shared/services/transactions.service';
import { ITransactionModel } from 'src/app/shared/interfaces/ITransactionModel';
import { SideWidgetService } from 'src/app/shared/services/side-widget.service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { UntypedFormControl } from '@angular/forms';
import { OrdersService } from '../../receive-edi/orders.service';
import Decimal from 'decimal.js';
import { IInventoryLocation } from 'src/app/shared/interfaces/inventory-location.interface';


@UntilDestroy()
@Component({
  selector: 'app-drug-records-table',
  templateUrl: './drug-records-table.component.html',
  styleUrls: ['./drug-records-table.component.scss', '../../../inventory.scss'],
})
export class DrugRecordsTableComponent extends BaseTableDirective implements OnInit {

  @Select(InventoryState.currentFilteredInventoryTransactions) currentFilteredInventoryTransactions$: Observable<ITransactionModel[]>;
  @Select(InventoryState.currentInventoryTransactions) allTransactions$: Observable<ITransactionModel[]>;
  @Select(InventoryState.currentInventory) currentInventory$: Observable<any>;
  @Select(InventoryState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(TransactionsState.widgetHistory) widgetHistory$: Observable<ITransaction[]>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  BadgeColorsEnum = BadgeColorsEnum;
  ActivityTypesEnum = ActivityTypesEnum;

  getDateFromISO = getDateFromISO;

  tableColumns = {
    transactionDate: false,
    user: true,
    activityType: true,
    startingQuantity: true,
    addedQuantity: true,
    endingQuantity: true,
    infoHeading: true,
    locationName: false
  };

  mostRecentTransaction: ITransactionModel;

  inventoryLocations = new UntypedFormControl(null);
  private sortColumn: string;

  inventoryLocationsEnabled: boolean;
  allLocationsOnHand: number = 0;
  selectedLocationsOnHand: number = 0;

  constructor(
    protected store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private transactionsService: TransactionsService,
    private sideWidgetService: SideWidgetService,
    private orderService: OrdersService,
  ) {
    super(store);
  }

  ngOnInit(): void {
    const drugId = this.route.snapshot.params?.id;

    this.store.dispatch(new GetCurrentInventoryTransactions(drugId))

    this.allTransactions$
      .pipe(untilDestroyed(this))
      .subscribe((transactions: ITransactionModel[]) => {
        this.mostRecentTransaction = transactions[0];
        if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
          this.allLocationsOnHand = transactions.reduce((prev, curr) => (new Decimal(+prev).add(new Decimal(+curr.addedQuantity))).toNumber(), 0);
          this.selectedLocationsOnHand = this.allLocationsOnHand;
           //SetCurrentFilteredInventoryTransactions will be hit in here
           this.filterDataByLocations(this.inventoryLocations.value || []);
        } else this.store.dispatch(new SetCurrentFilteredInventoryTransactions(transactions));
        
      });

    this.transactionsService.transactionSuccessfullyAmended$
    .pipe(untilDestroyed(this))
    .subscribe(() => {
     this.store.dispatch(new GetCurrentInventoryTransactions(drugId));
    });

    this.transactionsService.transactionDateSuccessfullyUpdated$
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      this.store.dispatch(new GetCurrentInventoryTransactions(drugId))
    });

    this.inventoryLocations
    .valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(locations => {
     this.filterDataByLocations(locations);
    });

    this.inventoryLocationsEnabled$
    .pipe(untilDestroyed(this))
    .subscribe((val: boolean) => this.inventoryLocationsEnabled = val)
  }

  openWidget(transactionId: string): void {
    this.sideWidgetService.openSideWidget(transactionId);
  }

  openAuditOrOrder(transaction: ITransactionModel): void {
    if (transaction?.auditId) {
      event.stopImmediatePropagation();
      this.router.navigate(['audits/details', transaction.auditId], {state : {
        navigatedFrom: this.router.url, 
        drugName: transaction.friendlyName}
      });
    } else if (transaction?.orderId) {
      event.stopImmediatePropagation();
      this.orderService.navigateToOrderDetails(this.router, transaction?.orderType, transaction.orderId, transaction.friendlyName);
    }
  }

  private filterDataByLocations(locations: IInventoryLocation[]): void {
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.filter(locations?.map(x => x?.id));
    }
  }

  private filter(locationIds: string[]): void {
    if (!locationIds.length) {
      this.store.dispatch(new SetCurrentFilteredInventoryTransactions(
        this.sort(
        this.store.selectSnapshot(InventoryState.currentInventoryTransactions),
        this.sortColumn
      )));
      this.selectedLocationsOnHand = this.allLocationsOnHand;
      return;
    }
    const filtered = this.store.selectSnapshot(InventoryState.currentInventoryTransactions)?.filter(x => locationIds?.includes(x?.inventoryLocationID));
    this.store.dispatch(new SetCurrentFilteredInventoryTransactions(this.sort(filtered, this.sortColumn)));
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.updateOnHandForSelectedLocations(filtered);
    }
  }

  private updateOnHandForSelectedLocations (transactions: ITransactionModel[]): void {
   this.selectedLocationsOnHand = transactions.reduce((total, transaction) => {
      return (new Decimal(total).add(new Decimal(+transaction.addedQuantity))).toNumber();
      }, 0);
  };

  sortByColumn(columnName: string): void {
    this.sortColumn = columnName;
    this.store.dispatch(new SetCurrentFilteredInventoryTransactions(this.sort(
      this.store.selectSnapshot(InventoryState.currentFilteredInventoryTransactions), 
      columnName
    )));
  }
}

